/**
 * Detección día/tarde/noche en la sala — Fase 12b extensión.
 *
 * El inline script de `index.html` ya aplica `data-time-of-day` al
 * `<html>` antes del primer paint. Este módulo arranca un interval
 * que re-evalúa cada minuto para que, si el usuario está con la
 * pestaña abierta durante el cambio de banda (p.ej. 18:59 → 19:00),
 * la sala crossfadee al estado correcto.
 *
 * Prioridad (igual que el inline):
 *  1. URL `?time=day|dusk|night`: override total — no hay watcher.
 *  2. localStorage `51esc.crt.timeOfDay` = 'day'/'dusk'/'night': forzado
 *     manual desde CRT → CONFIG → MODO DÍA/TARDE/NOCHE — no hay watcher.
 *  3. Preferencia 'auto' (o ausente): watcher activo, re-evalúa por
 *     hora del sistema cada minuto. Bandas (ver TIME_BANDS):
 *       - día   06:00 – 18:59
 *       - tarde 19:00 – 21:29
 *       - noche 21:30 – 05:59
 */

const CHECK_INTERVAL_MS = 60_000;

/**
 * Bandas horarias en minutos desde medianoche. La noche envuelve el
 * cambio de día — la lógica de `currentTimeOfDay` lo trata como
 * fallback (cualquier minuto fuera de día/tarde es noche).
 */
const DAY_START_MIN = 6 * 60; // 06:00
const DUSK_START_MIN = 19 * 60; // 19:00
const NIGHT_START_MIN = 21 * 60 + 30; // 21:30

type TimeOfDay = 'day' | 'dusk' | 'night';

function currentTimeOfDay(): TimeOfDay {
  const now = new Date();
  const min = now.getHours() * 60 + now.getMinutes();
  if (min >= DAY_START_MIN && min < DUSK_START_MIN) return 'day';
  if (min >= DUSK_START_MIN && min < NIGHT_START_MIN) return 'dusk';
  return 'night';
}

function urlOverride(): TimeOfDay | null {
  const p = new URLSearchParams(window.location.search).get('time');
  return p === 'day' || p === 'dusk' || p === 'night' ? p : null;
}

/**
 * Lee la preferencia persistida del CRT (`51esc.crt.timeOfDay`)
 * sin importar el módulo crt-controls (evitamos arrastrar todo el
 * código de brillo/contraste solo para una clave).
 */
function storedPreference(): 'auto' | TimeOfDay | 'cycle' {
  try {
    const raw = localStorage.getItem('51esc.crt');
    if (!raw) return 'auto';
    const parsed = JSON.parse(raw) as { timeOfDay?: unknown };
    const v = parsed.timeOfDay;
    if (v === 'day' || v === 'dusk' || v === 'night' || v === 'auto') return v;
    if (typeof v === 'string' && /^cycle-\d+$/.test(v)) return 'cycle';
  } catch {
    /* ignore */
  }
  return 'auto';
}

/**
 * Empieza a re-evaluar la hora del día cada minuto. Devuelve un
 * disposer (no usado, expuesto por simetría). Si hay URL override
 * o preferencia explícita (day/dusk/night), no hay watcher — la
 * elección es estable durante la sesión.
 */
export function startTimeOfDayWatcher(): () => void {
  if (urlOverride() !== null) return () => {};
  if (storedPreference() !== 'auto') return () => {};
  // Modo demo `?cycle`: el cycler lleva el control, no pisamos.
  if (new URLSearchParams(window.location.search).has('cycle')) return () => {};

  const root = document.documentElement;
  const apply = () => {
    const next = currentTimeOfDay();
    if (root.dataset.timeOfDay !== next) {
      root.dataset.timeOfDay = next;
    }
  };

  // El inline script ya aplicó el estado correcto, pero por si acaso
  // (p.ej. el atributo se borró en runtime) lo re-establecemos.
  apply();

  const id = window.setInterval(apply, CHECK_INTERVAL_MS);
  return () => window.clearInterval(id);
}
